import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormControl } from '@angular/forms';
import moment from 'moment';
import { PaymentRequestTaskService, PaymentRequestTask } from '../shared/_generated/backend';
import { DetailsDialogComponent } from '../details-dialog/details-dialog.component';
import { RecallDialogComponent } from '../recall-dialog/recall-dialog.component';
import { NotificationService } from '../shared/services/notification.sevice';

@Component({
    selector: 'app-payment-request-list',
    templateUrl: './payment-request-list.component.html',
    styleUrl: './payment-request-list.component.scss',
    standalone: false
})
export class PaymentRequestListComponent implements AfterViewInit {
    @ViewChild(MatPaginator) paginator: MatPaginator = new MatPaginator(new MatPaginatorIntl(), ChangeDetectorRef.prototype);
    @ViewChild(MatSort) sort: MatSort = new MatSort();

    displayedColumns: string[] = [
        'creationDateTime',
        'type',
        'payer',
        'amount',
        'comment',
        'status',
        'details',
        'recall'
    ];
    originalData: PaymentRequestTask[] = [];
    dataSource: MatTableDataSource<PaymentRequestTask> = new MatTableDataSource();
    isLoading = true;
    formControl = this.formBuilder.group({
        creationDate: new FormControl(''),
        payerName: new FormControl(''),
        comment: new FormControl('')
    });

    constructor(
        private paymentRequestTaskService: PaymentRequestTaskService,
        public dialog: MatDialog,
        private notificationService: NotificationService,
        private formBuilder: FormBuilder
    ) {
        this.formControl.valueChanges.subscribe(value => {
            let filteredData: PaymentRequestTask[] = this.originalData;

            if (value.creationDate) {
                const date = moment(value.creationDate).format('YYYYMMDD');
                filteredData = filteredData.filter(task => moment(task.creationDateTime).format('YYYYMMDD') === date);
            }

            if (value.payerName) {
                const formattedPayerName = value.payerName.trim().toLowerCase();
                filteredData = filteredData.filter(task => {
                    if (task.type === PaymentRequestTask.TypeEnum.PAYERATTENDED) {
                        if (task.payer?.emailAddress?.includes(formattedPayerName)) {
                            return task;
                        } else {
                            return null;
                        }
                    } else {
                        if (task.payer?.name.trim().toLowerCase().includes(formattedPayerName)) {
                            return task;
                        } else {
                            return null;
                        }
                    }
                });
            }

            if (value.comment) {
                const formattedComment = value.comment.trim().toLowerCase();
                filteredData = filteredData.filter(task => task.comment && task.comment.trim().toLowerCase().includes(formattedComment));
            }

            this.dataSource.data = filteredData;
        });
    }

    ngAfterViewInit() {
        this.paginator._intl.itemsPerPageLabel = 'Fizetési kérelmek oldalanként:';
        this.getTasks();
    }

    refresh() {
        this.getTasks();
    }

    getTasks() {
        this.paymentRequestTaskService.getTasks().subscribe({
            next: tasks => {
                this.originalData = tasks;
                this.dataSource = new MatTableDataSource(tasks);
                this.dataSource.filterPredicate =
                    (task, filter) => task.type.trim().toLowerCase().includes(filter);
                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    }

    openDetailsDialog(task: any) {
        this.dialog.open(DetailsDialogComponent, { data: task });
    }

    openRecallDialog(task: any) {
        const dialogRef: MatDialogRef<RecallDialogComponent, 'yes'> = this.dialog.open(RecallDialogComponent);
        dialogRef.afterClosed().subscribe(result => {
            if (result === 'yes') {
                this.recall(task.id);
            }
        });
    }

    async recall(taskId: string) {
        try {
            const response = await firstValueFrom(this.paymentRequestTaskService.recall(taskId));
            if (response && response.error) {
                this.notificationService.showErrorSnackBar(`Hiba: ${response.error}`);
            } else {
                this.notificationService.showSuccessSnackBar('A visszahívás sikeres volt.');
                this.getTasks();
            }
        } catch (error: any) {
            if (error instanceof HttpErrorResponse) {
                if (error.status === 400) {
                    const errorMessage = error.error.message || error.message;
                    this.notificationService.showErrorSnackBar(errorMessage);
                } else {
                    console.error(error);
                    this.notificationService.showErrorSnackBar('Kiszolgáló hiba.');
                }
            } else {
                console.error(error);
                this.notificationService.showErrorSnackBar('Váratlan hiba.');
            }
        }
    }
}
